/**
 * navigation search provider — search-as-you-type across the
 * host app's navigation items.
 *
 * Why this exists
 *   Before PR 3c the palette only surfaced navigation links via
 *   the empty-state quick-nav grid: the moment the user typed a
 *   query the nav links disappeared and only API hits were
 *   shown. The user's complaint "search doesn't even work here"
 *   was about exactly that gap. This provider closes it without
 *   forcing every host to reimplement the same fuzzy match.
 *
 * Contract
 *   Pass any list of nav-shaped items (or whatever the host
 *   already passes to <Sidebar />) to `createNavSearchProvider`
 *   and the resulting `SearchProvider` returns ranked nav hits
 *   for any non-empty query. Hit ordering: prefix > substring
 *   > keyword. The provider runs synchronously — no network —
 *   and ignores `signal`.
 */

import type { SearchProvider, SearchResult } from '../types'

/** Minimal shape the navigation provider needs. Compatible with
 *  `NavItem`, `ExternalLink`, `QuickNavItem`, and ad-hoc shapes
 *  the apps already wire into the layout. */
export interface NavSearchSource {
  id?: string
  label: string
  description?: string
  href?: string
  url?: string
  /** Used for "Tools / Module / Settings" grouping in the
   *  result row's subtitle. */
  group?: string
  /** Optional keyword list used for fuzzy hits. */
  keywords?: string[]
  /** Lucide icon string name (matches `getIcon(...)` registry).
   *  Optional — falls back to a generic icon. */
  icon?: string
}

export interface CreateNavProviderOptions {
  /** Provider id used for filter chip + dedupe. Default
   *  `"navigation"`. */
  id?: string
  /** Filter chip label. Default `"Navigation"`. */
  label?: string
  /** Maximum number of nav hits returned per query. Default 8. */
  limit?: number
  /** Override the result `type` so consumers can theme nav hits
   *  separately (e.g. `"page"` vs `"job"`). Default `"page"`. */
  resultType?: string
  /** Override the icon string baked into each result. Default
   *  picks `item.icon` when present (and a string), otherwise
   *  `"Compass"`. */
  fallbackIcon?: string
  /** When set, STRONG matches (exact / prefix label hit) are tagged
   *  with this `SearchResult.section` so the sectioned palette floats
   *  them to the top (e.g. `"pages"`). Weaker fuzzy matches stay
   *  unsectioned so they never bury the content tiers. Default
   *  undefined — no section, legacy flat behaviour. */
  section?: string
}

/** Whole-phrase label scores — exact match, prefix match, plain
 *  substring, and description/keyword-only fallback, in that order. */
const SCORE_EXACT_LABEL = 100
const SCORE_LABEL_PREFIX = 80
const SCORE_LABEL_SUBSTRING = 60
const SCORE_HAYSTACK_ONLY = 40
/** Multi-word token fallback (see `allTokensPresent` below): every
 *  token in the label itself, vs. scattered across description/
 *  keywords too. */
const SCORE_ALL_TOKENS_IN_LABEL = 70
const SCORE_ALL_TOKENS_IN_HAYSTACK = 30

/** Score at/above which a nav hit is considered STRONG enough to float
 *  to the top section — either a confident whole-phrase label match
 *  (>= `SCORE_LABEL_PREFIX`) or a multi-word query where every token is
 *  in the label itself (`SCORE_ALL_TOKENS_IN_LABEL`). Weaker fuzzy
 *  keyword/description-only matches stay unsectioned so they never bury
 *  the content tiers below. */
const STRONG_NAV_SCORE = SCORE_ALL_TOKENS_IN_LABEL

/**
 * BUG-2026-07-06-QUOTEBUILDER-SEARCH-UNREACHABLE: the whole-phrase
 * substring check below (`haystack.includes(q)`) requires the operator's
 * ENTIRE typed query to appear contiguously in the haystack. That fails
 * for a perfectly reasonable multi-word query the moment word order
 * differs, or the words are split across the label vs. description/
 * keywords (e.g. label "Quote Builder" + description "AI-assisted quote
 * creation…" — typing "builder quote" or "quote ai" would score 0 even
 * though every word is right there). Tokenising and requiring EVERY word
 * to appear somewhere (order-independent AND match) is the generalised
 * fix — it covers this hub and every other multi-word nav/hub label,
 * not just this one site.
 */
function allTokensPresent(haystack: string, tokens: string[]): boolean {
  return tokens.every((t) => haystack.includes(t))
}

/** Score a single nav row against the query. Higher is better;
 *  a score of 0 means "no match — drop the row". */
function score(item: NavSearchSource, q: string): number {
  const haystack = [
    item.label,
    item.description ?? '',
    ...(item.keywords ?? []),
  ]
    .join(' ')
    .toLowerCase()
  const label = item.label.toLowerCase()

  if (haystack.includes(q)) {
    if (label === q) return SCORE_EXACT_LABEL
    if (label.startsWith(q)) return SCORE_LABEL_PREFIX
    if (label.includes(q)) return SCORE_LABEL_SUBSTRING
    return SCORE_HAYSTACK_ONLY
  }

  // Multi-word fallback: every token present somewhere, any order.
  const tokens = q.split(/\s+/).filter(Boolean)
  if (tokens.length > 1 && allTokensPresent(haystack, tokens)) {
    return allTokensPresent(label, tokens)
      ? SCORE_ALL_TOKENS_IN_LABEL
      : SCORE_ALL_TOKENS_IN_HAYSTACK
  }

  return 0
}

/**
 * Build a `SearchProvider` from a list of navigation items.
 *
 * Hosts typically already pass the same list (or a superset of
 * it) to `<DashboardLayout navItems=…externalLinks=…/>`. By
 * routing the same data through the palette we keep one source
 * of truth and avoid drifting between sidebar and palette.
 *
 * Note: routing back to the URL is the host's responsibility —
 * `<GlobalSearch>` already navigates to `result.url` on Enter.
 */
export function createNavSearchProvider(
  items: NavSearchSource[],
  options: CreateNavProviderOptions = {},
): SearchProvider {
  const id = options.id ?? 'navigation'
  const label = options.label ?? 'Navigation'
  const limit = options.limit ?? 8
  const resultType = options.resultType ?? 'page'
  const fallbackIcon = options.fallbackIcon ?? 'Compass'
  const section = options.section

  return {
    id,
    label,
    search: async (rawQuery: string): Promise<SearchResult[]> => {
      const q = rawQuery.trim().toLowerCase()
      if (!q) return []

      const scored: Array<{ item: NavSearchSource; score: number }> = []
      for (const item of items) {
        const url = item.href ?? item.url
        if (!url) continue
        const s = score(item, q)
        if (s > 0) scored.push({ item, score: s })
      }
      scored.sort((a, b) => b.score - a.score)

      return scored.slice(0, limit).map(({ item, score: s }) => ({
        id: item.id ?? item.href ?? item.url ?? item.label,
        type: resultType,
        title: item.label,
        subtitle: item.group ?? item.description,
        description: item.description,
        // `item.icon` may be a React component (e.g. an ExternalLink
        // shape) — only honour string icon names; otherwise fall back
        // so `getIcon(...)` never receives a component.
        icon: typeof item.icon === 'string' ? item.icon : fallbackIcon,
        color: 'teal',
        url: item.href ?? item.url ?? '#',
        providerId: id,
        // Only float the confident hits — see `STRONG_NAV_SCORE` above.
        ...(section && s >= STRONG_NAV_SCORE ? { section } : {}),
      }))
    },
  }
}
